import type { MultiSelectChangeEvent } from "@tql/ui-components-react/kendo/dropdowns";
import { MultiSelect } from "@tql/ui-components-react/kendo/dropdowns";
import type { GridFilterCellProps } from "@tql/ui-components-react/kendo/grid";
import { useAtom } from "jotai";

import { accessorialsFilterIdsAtom } from "~/features/auto-dispatch/state/accessorials-filter-atom.ts";
import type { AutoDispatchGlobalAccessorials } from "~/shared/schemas/auto-dispatch-schema.ts";

type AccessorialsFilterCellProps = GridFilterCellProps & {
	accessorials: AutoDispatchGlobalAccessorials;
};

/**
 * Filter cell for the accessorials column. Selection is stored in
 * `accessorialsFilterIdsAtom` rather than Kendo's composite filter descriptor
 * — Kendo's `filterBy` can't compare against an array-of-objects field, and
 * any descriptor we push in gets re-applied by Kendo and wipes the grid. The
 * grid reads the atom itself and post-filters the data array.
 */
export const AccessorialsFilterCell = ({
	accessorials,
}: AccessorialsFilterCellProps) => {
	const [selectedIds, setSelectedIds] = useAtom(accessorialsFilterIdsAtom);
	const selected = accessorials.filter((a) => selectedIds.includes(a.id));

	const handleChange = (e: MultiSelectChangeEvent) => {
		setSelectedIds(
			(e.value as AutoDispatchGlobalAccessorials).map((a) => a.id),
		);
	};

	return (
		// Mirror the wrapper Kendo emits for its default filter cell so the
		// TraxGrid search-icon injector and filter-tab nav (both target
		// `.k-filtercell`) continue to pick up this column.
		<div className={"k-filtercell filter-cell-padded"}>
			<div className={"k-filtercell-wrapper"}>
				<MultiSelect
					data={accessorials}
					dataItemKey={"id"}
					onChange={handleChange}
					textField={"description"}
					value={selected}
				/>
			</div>
		</div>
	);
};
